import React, { useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, Grid, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@mui/icons-material/Add';
import ReactQuill from 'react-quill';
import EmojiPicker from 'emoji-picker-react';
import 'react-quill/dist/quill.snow.css';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import MainContainer from '../../components/MainContainer';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0, 4, 4, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100%',
    backgroundColor: theme.palette.background.default,
  },
  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(4),
    borderRadius: "0 0 24px 24px",
    color: "white",
    width: "100%",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  headerTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    color: "white",
    marginBottom: theme.spacing(0.5),
  },
  headerSubtitle: {
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.9)",
    opacity: 0.9,
  },
  inputCard: {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '24px',
    padding: theme.spacing(3),
    boxShadow: theme.palette.type === 'light' 
      ? '0 10px 40px rgba(0,0,0,0.04)' 
      : '0 10px 40px rgba(0,0,0,0.3)',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(4),
  },
  editorContainer: {
    width: '100%',
    marginBottom: theme.spacing(2),
    '& .ql-toolbar': {
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.type === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.05)',
      '& .ql-stroke': {
        stroke: theme.palette.text.primary,
      },
      '& .ql-fill': {
        fill: theme.palette.text.primary,
      },
      '& .ql-picker': {
        color: theme.palette.text.primary,
      }
    },
    '& .ql-container': {
      borderBottomLeftRadius: '16px',
      borderBottomRightRadius: '16px',
      border: `1px solid ${theme.palette.divider}`,
      borderTop: 'none',
      backgroundColor: theme.palette.type === 'light' ? '#fff' : 'rgba(255, 255, 255, 0.02)',
      fontSize: '16px',
      fontFamily: 'inherit',
      '& .ql-editor': {
        minHeight: '120px',
        color: theme.palette.text.primary,
        '&.ql-blank::before': {
          color: theme.palette.text.secondary,
          opacity: 0.6,
          fontStyle: 'normal',
        }
      }
    },
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    position: 'relative',
  },
  actionButton: {
    borderRadius: '12px',
    padding: '10px 24px',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    boxShadow: '0 8px 16px rgba(101, 23, 171, 0.25)',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 20px rgba(101, 23, 171, 0.35)',
    },
  },
  emojiButton: {
    backgroundColor: theme.palette.type === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)',
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.type === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.12)',
    },
  },
  emojiPickerContainer: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    marginBottom: theme.spacing(2),
    zIndex: 1000,
    boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  listContainer: {
    width: '100%',
    maxWidth: '800px',
  },
  listItem: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '16px',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateX(8px)',
      backgroundColor: theme.palette.type === 'light' 
        ? 'hsla(270, 80%, 60%, 0.02)' 
        : 'hsla(270, 80%, 60%, 0.08)',
      borderColor: theme.palette.primary.main,
    },
  },
  taskText: {
    '& .ql-editor': {
      padding: 0,
      fontSize: '1rem',
      color: theme.palette.text.primary,
    },
    '& img': {
      maxWidth: '100%',
      borderRadius: '8px',
      marginTop: '8px',
    }
  },
  taskDate: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    opacity: 0.6,
  },
  itemActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  iconButton: {
    padding: '8px',
    borderRadius: '10px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
    }
  },
}));

const ToDoList = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [task, setTask] = useState('');
  const [tasks, setTasks] = useState([]);
  const [editIndex, setEditIndex] = useState(-1);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    const savedTasks = localStorage.getItem('tasks');
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('tasks', JSON.stringify(tasks));
  }, [tasks]);

  const handleAddTask = () => {
    if (!task.trim()) {
      return;
    }

    const now = new Date();
    if (editIndex >= 0) {
      const newTasks = [...tasks];
      newTasks[editIndex] = {
        text: task,
        updatedAt: now,
        createdAt: newTasks[editIndex].createdAt,
      };
      setTasks(newTasks);
      setTask('');
      setEditIndex(-1);
    } else {
      setTasks([...tasks, { text: task, createdAt: now, updatedAt: now }]);
      setTask('');
    }
  };

  const handleEditTask = (index) => {
    setTask(tasks[index].text);
    setEditIndex(index);
  };

  const handleDeleteTask = (index) => {
    const newTasks = [...tasks];
    newTasks.splice(index, 1);
    setTasks(newTasks);
  };

  const handleEmojiClick = (emojiData) => {
    setTask((prevTask) => prevTask + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  return (
    <MainContainer>
      <Box className={classes.headerContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Typography className={classes.headerTitle}>Sua Lista de Tarefas</Typography>
            <Typography className={classes.headerSubtitle}>Gerencie suas atividades diárias com facilidade e estilo.</Typography>
          </Grid>
        </Grid>
      </Box>

      <div className={classes.root}>

      {/* Card de Entrada */}
      <div className={classes.inputCard}>
        <div className={classes.editorContainer}>
          <ReactQuill
            className={classes.editor}
            value={task}
            onChange={setTask}
            theme="snow"
            placeholder="O que você precisa fazer hoje?"
          />
        </div>

        <div className={classes.buttonGroup}>
          <Button
            startIcon={<InsertEmoticonIcon />}
            className={`${classes.actionButton} ${classes.emojiButton}`}
            onClick={() => setShowEmojiPicker((prev) => !prev)}
          >
            {showEmojiPicker ? 'Fechar' : 'Emoji'}
          </Button>

          <Button
            startIcon={editIndex >= 0 ? <EditIcon /> : <AddIcon />}
            variant="contained"
            className={`${classes.actionButton} ${classes.addButton}`}
            onClick={handleAddTask}
          >
            {editIndex >= 0 ? 'Salvar Alterações' : 'Adicionar Tarefa'}
          </Button>

          {showEmojiPicker && (
            <div className={classes.emojiPickerContainer}>
              <EmojiPicker 
                onEmojiClick={handleEmojiClick} 
                theme={theme.palette.type === 'dark' ? 'dark' : 'light'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Lista de Tarefas */}
      <div className={classes.listContainer}>
        <List>
          {tasks.map((t, index) => (
            <ListItem key={index} className={classes.listItem}>
              <ListItemText
                primary={
                  <div
                    className={classes.taskText}
                    dangerouslySetInnerHTML={{ __html: t.text }}
                  />
                }
                secondary={
                  <span className={classes.taskDate}>
                    {new Date(t.updatedAt).toLocaleString()}
                  </span>
                }
              />
              <ListItemSecondaryAction className={classes.itemActions}>
                <IconButton 
                  className={classes.iconButton}
                  onClick={() => handleEditTask(index)}
                  title="Editar"
                >
                  <EditIcon style={{ color: theme.palette.primary.main }} />
                </IconButton>
                <IconButton 
                  className={classes.iconButton}
                  onClick={() => handleDeleteTask(index)}
                  title="Excluir"
                >
                  <DeleteIcon style={{ color: '#ef4444' }} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  </MainContainer>
  );
};

export default ToDoList;